import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check, AlertCircle, ChevronDown, ChevronUp, History } from 'lucide-react';
import { Button } from '../ui';
import { Metronome } from './Metronome';
import { TestOutDocumentationForm } from './TestOutDocumentationForm';
import { getEmbedUrl } from '../../data/trainingSessionHelpers';

export function TestOutExercisePlayer({
  phaseNum,
  exercises,
  currentIndex,
  exerciseData,
  injurySide,
  onExerciseComplete,
  onNavigate,
  onExit,
}) {
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [showPastAttempts, setShowPastAttempts] = useState(false);
  const [localData, setLocalData] = useState({});

  const currentExercise = exercises[currentIndex];
  const existingData = exerciseData[currentExercise?.id];
  const isDocumented = !!existingData;

  // Reset local data when exercise changes
  useEffect(() => {
    setLocalData(existingData || {});
    setShowDocumentation(false);
  }, [currentIndex, currentExercise?.id]);

  if (!currentExercise) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <p className="text-gray-500">No exercise found</p>
      </div>
    );
  }

  const handleSaveDocumentation = (data) => {
    setLocalData(data);
    setShowDocumentation(false);
  };

  const handleComplete = () => {
    if (Object.keys(localData).length > 0) {
      onExerciseComplete(currentExercise.id, localData);
    }
  };

  const handleNext = () => {
    // Save current data before moving
    if (Object.keys(localData).length > 0) {
      onExerciseComplete(currentExercise.id, localData);
    } else {
      onNavigate(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    onNavigate(currentIndex - 1);
  };

  const isLastExercise = currentIndex === exercises.length - 1;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
          <span>Exercise {currentIndex + 1} of {exercises.length}</span>
          <span>{Math.round(((currentIndex + 1) / exercises.length) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="h-1.5 rounded-full bg-primary transition-all"
            style={{ width: `${((currentIndex + 1) / exercises.length) * 100}%` }}
          />
        </div>

        {/* Exercise Pills */}
        <div className="flex gap-1.5 mt-3 overflow-x-auto pb-1">
          {exercises.map((ex, idx) => {
            const isCompleted = !!exerciseData[ex.id];
            const isCurrent = idx === currentIndex;

            return (
              <button
                key={ex.id}
                onClick={() => onNavigate(idx)}
                className={`
                  flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all
                  ${isCurrent
                    ? 'bg-primary text-white'
                    : isCompleted
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                `}
              >
                {isCompleted && !isCurrent && <Check className="h-3 w-3 inline mr-1" />}
                {idx + 1}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* Exercise Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              {currentExercise.displayName}
            </h2>

            {/* Important Note - only for multi-level exercises */}
            {(currentExercise.exercise === '1-Leg Squat' || currentExercise.exercise === '1-Leg Bridge') && (
              <div className="flex items-start bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  Document only the highest level of the exercise you perform.
                </p>
              </div>
            )}
          </div>

          {/* Video */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="aspect-video bg-gray-900">
              <iframe
                src={getEmbedUrl(currentExercise.video)}
                title={currentExercise.displayName}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>

          {/* Metronome (if needed) */}
          {currentExercise.metronome && (
            <div className="flex justify-center">
              <Metronome bpm={60} />
            </div>
          )}

          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Instructions</h3>
            <p className="text-sm text-gray-600 whitespace-pre-line">
              {currentExercise.description}
            </p>
          </div>

          {/* Documentation Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Documentation</h3>
                {isDocumented || Object.keys(localData).length > 0 ? (
                  <p className="text-sm text-green-600 flex items-center mt-1">
                    <Check className="h-4 w-4 mr-1" />
                    Documented
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">
                    Not yet documented
                  </p>
                )}
              </div>
              <Button
                variant={isDocumented || Object.keys(localData).length > 0 ? "outline" : "primary"}
                onClick={() => setShowDocumentation(true)}
              >
                {isDocumented || Object.keys(localData).length > 0 ? 'Edit' : 'Document'}
              </Button>
            </div>
          </div>

          {/* Past Attempts (placeholder - would need API integration) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShowPastAttempts(!showPastAttempts)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            >
              <div className="flex items-center">
                <History className="h-5 w-5 text-gray-400 mr-2" />
                <span className="font-medium text-gray-900">Past Attempts</span>
              </div>
              {showPastAttempts ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>

            {showPastAttempts && (
              <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                <p className="text-sm text-gray-500 text-center py-4">
                  Past attempt data will be shown here once you complete test outs.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="bg-white border-t border-gray-200 p-4 flex gap-3">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          className="flex-shrink-0"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>

        {isLastExercise ? (
          <Button
            onClick={handleComplete}
            disabled={Object.keys(localData).length === 0}
            fullWidth
          >
            <Check className="h-5 w-5 mr-2" />
            Complete Test Out
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            fullWidth
          >
            Next Exercise
            <ChevronRight className="h-5 w-5 ml-2" />
          </Button>
        )}
      </div>

      {/* Documentation Modal */}
      {showDocumentation && (
        <TestOutDocumentationForm
          exercise={currentExercise}
          existingData={localData}
          injurySide={injurySide}
          onSave={handleSaveDocumentation}
          onClose={() => setShowDocumentation(false)}
        />
      )}
    </div>
  );
}